import React from "react";
import { useGetParams } from "@hooks";
import { Datejs } from "@nubeteck/utils";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { DialogProps } from "primereact/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { PickNullable, IExchangeRates } from "@interfaces";
import { Form, Dialog, Calendar, InputNumber } from "@components";

import { useAccountingQuery, useAccountingMutation } from "../../hooks";

export interface IExchangeRatesDialogProps extends DialogProps {
  exchangeRateId?: number;
}

type FormValues = PickNullable<
  IExchangeRates,
  | "Fecha"
  | "MonedaId"
  | "EstadoId"
  | "PrecioVenta"
  | "PrecioCompra"
  | "TasaCambioId"
>;

const model = {
  from: (data: IExchangeRates): FormValues => {
    return {
      MonedaId: data.MonedaId,
      EstadoId: data.EstadoId,
      PrecioVenta: data.PrecioVenta,
      PrecioCompra: data.PrecioCompra,
      Fecha: Datejs(data.Fecha).toDate(),
      TasaCambioId: data.TasaCambioId ?? 0,
    };
  },
  to: (data: FormValues): IExchangeRates => {
    return {
      MonedaId: data.MonedaId ?? 0,
      EstadoId: data.EstadoId ?? 1,
      Fecha: Datejs(data.Fecha).toDate(),
      PrecioVenta: data.PrecioVenta ?? 0,
      TasaCambioId: data.TasaCambioId ?? 0,
      PrecioCompra: data.PrecioCompra ?? 0,
    };
  },
};

const ExchangeRatesDialog = ({
  exchangeRateId,
  ...props
}: IExchangeRatesDialogProps) => {
  const currencyId = useGetParams("id");
  const queryClient = useQueryClient();

  const getDollarRate = useAccountingQuery("ExchangeRates", "getDollarRate");
  const createExchangeRate = useAccountingMutation("ExchangeRates", "create");
  const updateExchangeRate = useAccountingMutation("ExchangeRates", "update");
  const getCreateExchangeRate = useAccountingQuery(
    "ExchangeRates",
    "getCreate",
  );
  const exchangeRate = useAccountingQuery(
    "ExchangeRates",
    "getById",
    exchangeRateId,
    { enabled: !!exchangeRateId },
  );

  const form = useForm<FormValues>({
    defaultValues: {
      EstadoId: 1,
      MonedaId: currencyId,
      Fecha: Datejs().toDate(),
      PrecioVenta: currencyId == 1 ? 1 : 0,
      PrecioCompra: currencyId == 1 ? 1 : 0,
    },
  });
  const { reset } = form;

  React.useEffect(() => {
    if (exchangeRate.data) {
      reset(model.from(exchangeRate.data));
    }
  }, [exchangeRate.data, reset]);

  const handleSuccess = () => {
    form.reset();
    props.onHide();
    queryClient.invalidateQueries({ queryKey: ["Currency"] });
  };

  const onSubmit = (values: FormValues) => {
    if (exchangeRateId) {
      return updateExchangeRate.mutateAsync(model.to(values), {
        onSuccess: handleSuccess,
      });
    }
    return createExchangeRate.mutateAsync(model.to(values), {
      onSuccess: handleSuccess,
    });
  };
  const isDolar = getCreateExchangeRate.data?.MonedasSelect?.some(
    (moneda) =>
      moneda.MonedaNombre === "Dolar Americano" &&
      moneda.MonedaId == currencyId,
  );

  const handleObtenerTasa = React.useCallback(() => {
    if (isDolar) {
      form.setValue("PrecioCompra", getDollarRate.data?.dollarPurchase);
      form.setValue("PrecioVenta", getDollarRate.data?.dollarSelling);
    }
  }, [
    form,
    getDollarRate.data?.dollarPurchase,
    getDollarRate.data?.dollarSelling,
    isDolar,
  ]);

  return (
    <Dialog
      {...props}
      style={{ width: "50rem" }}
      subtitle="Rellena los datos del formulario"
      onHide={() => {
        if (exchangeRateId) form.reset();
        props.onHide();
      }}
      title={
        exchangeRateId
          ? `Editando tasa de cambio`
          : "Creando nueva tasa de cambio"
      }
      footer={() => (
        <div className="flex flex-1 justify-content-end gap-2">
          <Button
            size="small"
            severity="warning"
            label="Reiniciar formulario"
            onClick={() => {
              reset();
            }}
          />
          <Button
            size="small"
            onClick={form.handleSubmit(onSubmit)}
            label={exchangeRateId ? "Guardar cambios" : "Crear tasa de cambio"}
          />
        </div>
      )}
    >
      <Form form={form}>
        <Calendar
          name="Fecha"
          label="Fecha"
          dateFormat="dd/mm/yy"
          className="p-inputtext-sm"
          rules={{ required: "Este campo es necesario" }}
        />
        <Button
          size="small"
          type="button"
          severity="info"
          visible={isDolar}
          className="mb-2 w-auto"
          label="Obtener la tasa"
          onClick={handleObtenerTasa}
          loading={getDollarRate.isFetching}
          disabled={getDollarRate.isFetching}
        />
        <div className="flex gap-3">
          <InputNumber
            id="PrecioCompra"
            name="PrecioCompra"
            minFractionDigits={2}
            label="Tasas de compra"
            className="p-inputtext-sm"
            rules={{ required: "Este campo es necesario" }}
          />
          <InputNumber
            id="PrecioVenta"
            name="PrecioVenta"
            minFractionDigits={2}
            label="Tasas de venta"
            className="p-inputtext-sm"
            rules={{ required: "Este campo es necesario" }}
          />
        </div>
      </Form>
    </Dialog>
  );
};

export default ExchangeRatesDialog;
